import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import OpportunityModal from '../components/crm/OpportunityModal';
import { STAGES, OPEN_STAGES, formatMoney, formatDate, todayStr } from '../components/crm/crmConstants';

export default function Pipeline() {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const [opportunities, setOpportunities] = useState([]);
  const [users, setUsers] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  const [modal, setModal] = useState(null); // null | 'new' | opportunity
  const [dragId, setDragId] = useState(null);

  const fetchData = useCallback(async () => {
    const res = await api.get('/api/crm/opportunities', { params: ownerFilter ? { owner_id: ownerFilter } : {} });
    setOpportunities(res.data);
  }, [ownerFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { api.get('/api/crm/users').then(r => setUsers(r.data)); }, []);

  const moveTo = async (opp, stage) => {
    if (opp.stage === stage) return;
    const payload = { stage };
    if (stage === 'lost') {
      const reason = window.prompt('Kaybedilme nedeni (isteğe bağlı):', opp.lost_reason || '');
      if (reason === null) return;
      payload.lost_reason = reason || null;
    }
    // optimistic update so the card doesn't jump back while the request runs
    setOpportunities(prev => prev.map(o => o.id === opp.id ? { ...o, stage } : o));
    try {
      await api.put(`/api/crm/opportunities/${opp.id}`, payload);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
    fetchData();
  };

  const q = search.trim().toLocaleLowerCase('tr');
  const filtered = opportunities.filter(o =>
    !q || o.title.toLocaleLowerCase('tr').includes(q) || (o.customer_name || '').toLocaleLowerCase('tr').includes(q)
  );
  const columns = STAGES.filter(s => showClosed || OPEN_STAGES.includes(s.key));

  const totalsByCurrency = (list, weighted = false) => {
    const totals = {};
    list.forEach(o => {
      const v = (o.value || 0) * (weighted ? (o.probability || 0) / 100 : 1);
      totals[o.currency] = (totals[o.currency] || 0) + v;
    });
    const entries = Object.entries(totals).filter(([, v]) => v > 0);
    return entries.length ? entries.map(([cur, v]) => formatMoney(v, cur)).join(' + ') : '—';
  };

  const openOpps = filtered.filter(o => OPEN_STAGES.includes(o.stage));
  const today = todayStr();

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-800">Satış Hunisi</h2>
        <button onClick={() => setModal('new')} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium">+ Yeni Fırsat</button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Fırsat veya müşteri ara..." className="border rounded px-3 py-2 text-sm w-64" />
        <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="">Tüm sorumlular</option>
          <option value={currentUser.id}>Benim fırsatlarım</option>
          {users.filter(u => u.id !== currentUser.id).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <label className="text-sm text-gray-600 flex items-center gap-1">
          <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> Kapananları göster
        </label>
        <div className="ml-auto text-sm text-gray-600">
          Açık: <span className="font-semibold text-gray-800">{totalsByCurrency(openOpps)}</span>
          <span className="mx-2 text-gray-300">|</span>
          Ağırlıklı: <span className="font-semibold text-gray-800">{totalsByCurrency(openOpps, true)}</span>
        </div>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {columns.map(stage => {
          const items = filtered.filter(o => o.stage === stage.key);
          return (
            <div key={stage.key}
              className="flex-1 min-w-[230px] bg-gray-50 rounded-lg border"
              onDragOver={e => e.preventDefault()}
              onDrop={() => { const opp = opportunities.find(o => o.id === dragId); if (opp) moveTo(opp, stage.key); setDragId(null); }}>
              <div className={`px-3 py-2 border-b-2 ${stage.border} rounded-t-lg bg-white`}>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-sm text-gray-700">{stage.label}</span>
                  <span className="text-xs text-gray-400">{items.length}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">{totalsByCurrency(items)}</div>
              </div>
              <div className="p-2 space-y-2 min-h-[120px]">
                {items.map(o => (
                  <div key={o.id} draggable onDragStart={() => setDragId(o.id)} onClick={() => setModal(o)}
                    className="bg-white rounded border p-3 shadow-sm hover:shadow cursor-pointer">
                    <div className="text-sm font-medium text-gray-800">{o.title}</div>
                    <button onClick={e => { e.stopPropagation(); navigate(`/customers/${o.customer_id}`); }}
                      className="text-xs text-blue-600 hover:underline text-left">{o.customer_name}</button>
                    <div className="flex justify-between items-center mt-2 text-xs">
                      <span className="font-semibold text-gray-700">{formatMoney(o.value, o.currency)}</span>
                      <span className="text-gray-400">%{o.probability}</span>
                    </div>
                    <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
                      <span className={o.expected_close_date && o.expected_close_date < today && OPEN_STAGES.includes(o.stage) ? 'text-red-600 font-medium' : ''}>
                        {o.expected_close_date ? `📅 ${formatDate(o.expected_close_date)}` : ''}
                      </span>
                      <span>{o.owner_name}</span>
                    </div>
                    {o.quotes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {o.quotes.map(qt => (
                          <button key={qt.id} onClick={e => { e.stopPropagation(); navigate(`/quotes/${qt.id}`); }}
                            className="font-mono text-[11px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded hover:bg-indigo-100">{qt.quote_number}</button>
                        ))}
                      </div>
                    )}
                    <div className="mt-2 flex gap-1" onClick={e => e.stopPropagation()}>
                      <select value={o.stage} onChange={e => moveTo(o, e.target.value)} className="flex-1 border rounded text-xs px-1 py-0.5 text-gray-600">
                        {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                      {OPEN_STAGES.includes(o.stage) && (
                        <button onClick={() => navigate(`/quotes/new?customer_id=${o.customer_id}&opportunity_id=${o.id}`)}
                          className="text-xs text-green-700 bg-green-50 hover:bg-green-100 px-2 rounded" title="Bu fırsat için teklif oluştur">+ Teklif</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <OpportunityModal
          opportunity={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchData(); }}
        />
      )}
    </div>
  );
}
